import React from 'react';
import { BarChart } from 'lucide-react';
// FT-356: back navigation moved to shell-level MobileBottomBar via
// NavigationBackContext. Header BackButton removed.
import { useSetBackHandler } from '../../../../contexts/NavigationBackContext';

// Trends Analytics Content Component
// FT-356: register onBack (if any) as the shell-level back handler.
export const TrendsAnalyticsContent = ({ data, onBack }) => {
  useSetBackHandler(onBack);
  if (!data) {
    return (
      <div className="space-y-6">
        {/* FT-356: Back navigation moved to shell-level MobileBottomBar. */}
        <div className="bg-white shadow rounded-lg p-12 text-center">
          <BarChart className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">Loading analytics data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* FT-356: Back navigation moved to shell-level MobileBottomBar. */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center mb-4">
          <BarChart className="h-6 w-6 text-gray-400 mr-2" />
          <h3 className="text-lg font-medium text-gray-900">Trends & Analytics</h3>
        </div>
        <p className="text-gray-600 mb-6">Performance metrics and safety trends over time</p>

        {/* Trend Cards - 3x3 Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Row 1: Safety & Operations */}
          <TrendCard
            title="Drill Performance"
            value={`${data.drills.completed}/${data.drills.total}`}
            trend={data.drills.overdue > 0 ? 'down' : 'up'}
            description={data.drills.overdue > 0
              ? `${data.drills.overdue} drill(s) overdue`
              : "All drills current"}
          />
          <TrendCard
            title="Defect Resolution"
            value={`${data.defects.total - data.defects.open}/${data.defects.total}`}
            trend={data.defects.critical > 0 ? 'down' : 'neutral'}
            description={data.defects.open > 0
              ? `${data.defects.open} open (${data.defects.critical} critical)`
              : "No open defects"}
          />
          <TrendCard
            title="Incidents YTD"
            value={data.incidents.year}
            trend="neutral"
            description={data.incidents.open > 0
              ? `${data.incidents.open} open investigation(s)`
              : "No open incidents"}
          />

          {/* Row 2: Crew Compliance */}
          <TrendCard
            title="Crew Certificates"
            value={data.crew.certificates.total > 0
              ? `${Math.round((data.crew.certificates.total - data.crew.certificates.expired) / data.crew.certificates.total * 100)}%`
              : 'N/A'}
            trend={data.crew.certificates.total === 0
              ? 'neutral'
              : (data.crew.certificates.expired === 0 ? 'up' : 'down')}
            description={data.crew.certificates.total > 0
              ? (data.crew.certificates.expired > 0
                  ? `${data.crew.certificates.expired} expired`
                  : "All certificates valid")
              : "No certificates on file"}
          />
          <TrendCard
            title="Hours of Rest"
            value={(() => {
              const total = data.crew.hoursOfRest.compliant + data.crew.hoursOfRest.violations;
              if (total === 0) return 'N/A';
              return `${Math.round((data.crew.hoursOfRest.compliant / total) * 100)}%`;
            })()}
            trend={data.crew.hoursOfRest.violations > 0 ? 'down' : 'up'}
            description={data.crew.hoursOfRest.violations > 0
              ? `${data.crew.hoursOfRest.violations} violation(s) this week`
              : "No violations"}
          />
          <TrendCard
            title="Crew Familiarisation"
            value={data.crew.familiarisation.total > 0
              ? `${data.crew.familiarisation.completed}/${data.crew.familiarisation.total}`
              : 'N/A'}
            trend={data.crew.familiarisation.total === 0
              ? 'neutral'
              : (data.crew.familiarisation.incomplete.length === 0 ? 'up' : 'down')}
            description={data.crew.familiarisation.total > 0
              ? (data.crew.familiarisation.incomplete.length > 0
                  ? `${data.crew.familiarisation.incomplete.length} pending`
                  : "All crew familiarised")
              : "No crew records"}
          />

          {/* Row 3: Equipment & Vessel */}
          <TrendCard
            title="Equipment Checks"
            value={data.equipment.total > 0
              ? `${data.equipment.checked}/${data.equipment.total}`
              : 'N/A'}
            trend={data.equipment.overdue > 0 ? 'down' : 'up'}
            description={data.equipment.overdue > 0
              ? `${data.equipment.overdue} check(s) overdue`
              : "All checks current"}
          />
          <TrendCard
            title="Vessel Certificates"
            value={data.vessel.certificates.total > 0
              ? `${Math.round((data.vessel.certificates.total - data.vessel.certificates.expired) / data.vessel.certificates.total * 100)}%`
              : 'N/A'}
            trend={data.vessel.certificates.total === 0
              ? 'neutral'
              : (data.vessel.certificates.expired === 0 ? 'up' : 'down')}
            description={data.vessel.certificates.total > 0
              ? (data.vessel.certificates.expired > 0
                  ? `${data.vessel.certificates.expired} expired - DETENTION RISK`
                  : "All certificates valid")
              : "No certificates tracked"}
          />
          <TrendCard
            title="Document Compliance"
            value={data.documents.total > 0
              ? `${data.documents.active}/${data.documents.total}`
              : 'N/A'}
            trend={data.documents.overdue > 0 ? 'down' : 'up'}
            description={data.documents.overdue > 0
              ? `${data.documents.overdue} review(s) overdue`
              : "All documents current"}
          />
        </div>

        {/* Key Insights */}
        <div className="mt-8">
          <h4 className="text-sm font-medium text-gray-900 mb-4">Key Insights</h4>
          <ul className="space-y-2">
            {/* Critical Alerts - Vessel Certificates (highest priority) */}
            {data.vessel.certificates.expired > 0 && (
              <InsightItem
                type="danger"
                text={`${data.vessel.certificates.expired} vessel certificate(s) expired - DETENTION RISK`}
              />
            )}

            {/* Critical - Crew Certificates */}
            {data.crew.certificates.expired > 0 && (
              <InsightItem
                type="danger"
                text={`${data.crew.certificates.expired} crew certificate(s) have expired and need renewal`}
              />
            )}

            {/* Critical - Defects */}
            {data.defects.critical > 0 && (
              <InsightItem
                type="danger"
                text={`${data.defects.critical} critical defect(s) require immediate attention`}
              />
            )}

            {/* Critical - Equipment */}
            {data.equipment.overdue > 0 && (
              <InsightItem
                type="danger"
                text={`${data.equipment.overdue} equipment check(s) overdue`}
              />
            )}

            {/* Warnings - Drills */}
            {data.drills.overdue > 0 && (
              <InsightItem
                type="warning"
                text={`${data.drills.overdue} drill(s) are overdue for completion`}
              />
            )}

            {/* Warnings - Hours of Rest */}
            {data.crew.hoursOfRest.violations > 0 && (
              <InsightItem
                type="warning"
                text={`${data.crew.hoursOfRest.violations} hours of rest violation(s) in the last 7 days`}
              />
            )}

            {/* Warnings - Documents */}
            {data.documents.overdue > 0 && (
              <InsightItem
                type="warning"
                text={`${data.documents.overdue} document(s) overdue for review`}
              />
            )}

            {/* Warnings - Familiarisation */}
            {data.crew.familiarisation.incomplete.length > 0 && (
              <InsightItem
                type="warning"
                text={`${data.crew.familiarisation.incomplete.length} crew member(s) pending familiarisation`}
              />
            )}

            {/* Info - Expiring Soon */}
            {data.crew.certificates.expiringSoon > 0 && (
              <InsightItem
                type="info"
                text={`${data.crew.certificates.expiringSoon} crew certificate(s) expiring within 30 days`}
              />
            )}
            {data.vessel.certificates.expiringSoon > 0 && (
              <InsightItem
                type="info"
                text={`${data.vessel.certificates.expiringSoon} vessel certificate(s) expiring within 90 days`}
              />
            )}
            {data.drills.dueSoon > 0 && (
              <InsightItem
                type="info"
                text={`${data.drills.dueSoon} drill(s) due within 7 days`}
              />
            )}

            {/* No issues message */}
            {data.vessel.certificates.expired === 0 &&
             data.crew.certificates.expired === 0 &&
             data.defects.critical === 0 &&
             data.equipment.overdue === 0 &&
             data.drills.overdue === 0 &&
             data.crew.hoursOfRest.violations === 0 &&
             data.documents.overdue === 0 &&
             data.crew.familiarisation.incomplete.length === 0 && (
              <InsightItem
                type="info"
                text="No compliance issues detected - all systems green"
              />
            )}
          </ul>
        </div>
      </div>
    </div>
  );
};

// Trend Card Component
const TrendCard = ({ title, value, trend, description }) => {
  const getTrendIcon = () => {
    if (trend === 'up') return '↑';
    if (trend === 'down') return '↓';
    return '→';
  };

  const getTrendColor = () => {
    if (trend === 'up') return 'text-green-600';
    if (trend === 'down') return 'text-red-600';
    return 'text-gray-600';
  };

  return (
    <div className="bg-gray-50 rounded-lg p-4">
      <div className="flex justify-between items-start mb-2">
        <h5 className="text-sm font-medium text-gray-900">{title}</h5>
        <span className={`text-lg ${getTrendColor()}`}>{getTrendIcon()}</span>
      </div>
      <div className="text-2xl font-bold text-gray-900">{value}</div>
      <p className="text-xs text-gray-500 mt-1">{description}</p>
    </div>
  );
};

// Insight Item Component
const InsightItem = ({ type, text }) => {
  const getTypeStyles = () => {
    switch (type) {
      case 'danger': return 'bg-red-50 text-red-800 border-red-200';
      case 'warning': return 'bg-yellow-50 text-yellow-800 border-yellow-200';
      case 'info': return 'bg-blue-50 text-blue-800 border-blue-200';
      default: return 'bg-gray-50 text-gray-800 border-gray-200';
    }
  };

  return (
    <li className={`px-3 py-2 border rounded-lg text-sm ${getTypeStyles()}`}>
      {text}
    </li>
  );
};
